import React, { useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useAcademyStudents,
  useLevels,
  useSetStudentLevel,
} from "../hooks/useLevels";
import { usePlanLimits } from "../hooks/usePlanLimits";

function LevelSelect({ student, levels, onChange, busy }) {
  return (
    <Form.Select
      size="sm"
      value={student.level?.id ?? ""}
      onChange={(e) => onChange(student, e.target.value || null)}
      disabled={busy}
      style={{ maxWidth: 220 }}
      aria-label={`Nivel de ${student.name}`}
    >
      <option value="">Sin nivel</option>
      {levels.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
        </option>
      ))}
    </Form.Select>
  );
}

// Una fila "etiqueta · barra · valor" para el desglose de ocupación.
function BarRow({ label, value, total, variant }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between small mb-1">
        <span className="text-muted">{label}</span>
        <span className="fw-medium">{value}</span>
      </div>
      <div className="progress" style={{ height: 6 }}>
        <div className={`progress-bar ${variant}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Dos tarjetas: ocupación de cupos del plan + cuántos activos están realmente
// estudiando (tienen un plan de estudio con horas) vs. aún no.
function StudentStatsCards({ s }) {
  const limit = s.limit ?? null;
  const occ = limit ? Math.min(100, Math.round((s.used / limit) * 100)) : 100;
  const free = limit != null ? Math.max(0, limit - s.used) : null;

  return (
    <Row className="g-3 mb-3">
      <Col md={6} xl={4}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-baseline">
              <span className="text-muted small">Estudiantes activos</span>
              <span className="fw-bold" style={{ fontSize: "1.15rem" }}>
                {s.used} / {limit ?? "∞"}
              </span>
            </div>
            <div className="progress mt-2" style={{ height: 8 }}>
              <div className="progress-bar bg-success" style={{ width: `${occ}%` }} />
            </div>
            <div className="small text-muted mt-2">
              {free != null
                ? `Cupos usados de tu plan · ${free} libre${free === 1 ? "" : "s"}`
                : "Estudiantes activos en tu academia"}
            </div>
            {s.frozen > 0 && (
              <div className="small text-warning mt-1">
                <i className="mdi mdi-clock-alert-outline me-1" aria-hidden="true" />
                {s.frozen} en espera de cupo
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={6} xl={4}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body>
            <span className="text-muted small">Uso real de los cupos</span>
            <div className="mt-2">
              <BarRow
                label="Con plan de estudio activo"
                value={s.studying}
                total={s.used}
                variant="bg-primary"
              />
              <BarRow
                label="Registrados sin plan aún"
                value={s.without_plan}
                total={s.used}
                variant="bg-secondary"
              />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

function EstudiantesPage() {
  const { data: students = [], isLoading, isError } = useAcademyStudents();
  const { data: levels = [] } = useLevels();
  const { data: limits } = usePlanLimits();
  const s = limits?.students;
  const setLevelMutation = useSetStudentLevel();
  const [query, setQuery] = useState("");
  const [savingId, setSavingId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.document ?? "").toLowerCase().includes(q)
    );
  }, [students, query]);

  const handleChange = (student, levelId) => {
    setSavingId(student.id);
    setLevelMutation.mutate(
      { studentId: student.id, levelId },
      {
        onSuccess: () => toast.success(`Nivel actualizado — ${student.name}`),
        onError: () => toast.error("No se pudo actualizar el nivel."),
        onSettled: () => setSavingId(null),
      }
    );
  };

  return (
    <div>
      <div className="mb-3">
        <h4 className="mb-1">Estudiantes</h4>
        <p className="text-muted small mb-0">
          Consulta las horas contratadas, el saldo restante y asigna el nivel
          de cada estudiante. La evaluación se hace por fuera de la app; aquí
          registras el resultado.
        </p>
      </div>

      {s && <StudentStatsCards s={s} />}

      <Form.Control
        className="mb-3"
        placeholder="Buscar por nombre o documento…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ maxWidth: 360 }}
      />

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {isError && (
        <Alert variant="danger">No se pudieron cargar los estudiantes.</Alert>
      )}
      {!isLoading && !isError && filtered.length === 0 && (
        <Card>
          <p className="text-muted text-center py-4 mb-0">
            No hay estudiantes que coincidan.
          </p>
        </Card>
      )}

      {filtered.length > 0 && (
        <>
          {/* PC: tabla */}
          <Card className="d-none d-md-block">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Estudiante</th>
                    <th style={{ width: 130 }}>Contratadas</th>
                    <th style={{ width: 130 }}>Saldo</th>
                    <th style={{ width: 240 }}>Nivel</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="fw-medium">{s.name}</div>
                        {s.document && (
                          <div className="text-muted small">Doc. {s.document}</div>
                        )}
                      </td>
                      <td>{s.hours_purchased} h</td>
                      <td>
                        <Badge bg={s.hours_balance > 0 ? "success" : "secondary"}>
                          {s.hours_balance} h
                        </Badge>
                      </td>
                      <td>
                        <LevelSelect
                          student={s}
                          levels={levels}
                          onChange={handleChange}
                          busy={savingId === s.id}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Móvil: tarjetas */}
          <div className="d-md-none d-flex flex-column gap-2">
            {filtered.map((s) => (
              <Card key={s.id}>
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-medium">{s.name}</div>
                      {s.document && (
                        <div className="text-muted small">Doc. {s.document}</div>
                      )}
                    </div>
                    <Badge
                      bg={s.hours_balance > 0 ? "success" : "secondary"}
                      className="flex-shrink-0"
                    >
                      {s.hours_balance} h
                    </Badge>
                  </div>
                  <div className="text-muted small mb-2">
                    Contratadas: {s.hours_purchased} h
                  </div>
                  <Form.Label className="small text-muted mb-1">Nivel</Form.Label>
                  <LevelSelect
                    student={s}
                    levels={levels}
                    onChange={handleChange}
                    busy={savingId === s.id}
                  />
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export { EstudiantesPage };
